import { __assign, __rest } from "tslib";
/** @jsx jsx */

import * as React from 'react';
import { jsx, css } from '@emotion/react';
import { Positioner } from './Positions';
import { Text } from './Text';
import { animated } from 'react-spring';
import { useUid } from './hooks/use-uid';
import { useTheme } from './theme/Providers';
export var arrowStyles = function arrowStyles(color) {
  return css("\n    width: 1em;\n    height: 1em;\n    display: flex;\n    align-items: center;\n\n    &[data-placement*='bottom'] {\n      bottom: 100%;\n      left: 0;\n\n      &::before {\n        border-width: 1em 1em 0 1em;\n        border-color: ".concat(color, " transparent transparent transparent;\n        transform: rotate(180deg)\n      }\n    }\n    &[data-placement*='top'] {\n      top: 100%;\n      left: 0;\n\n      &::before {\n        border-width: 1em 1em 0 1em;\n        border-color: ").concat(color, " transparent transparent transparent;\n      }\n    }\n    &[data-placement*='right'] {\n      right: 100%;\n\n      &::before {\n        border-width: 1em 1em 1em 0;\n        border-color: transparent ").concat(color, " transparent transparent;\n      }\n    }\n    &[data-placement*='left'] {\n      left: 100%;\n\n      &::before {\n        border-width: 1em 0 1em 1em;\n        border-color: transparent transparent transparent ").concat(color, ";\n      }\n    }\n    &::before {\n      content: '';\n      display: block;\n      border-style: solid;\n    }\n  "));
};
export var Tooltip = function Tooltip(_a) {
  var placement = _a.placement,
      children = _a.children,
      content = _a.content,
      _b = _a.delayIn,
      delayIn = _b === void 0 ? 0 : _b,
      _c = _a.delayOut,
      delayOut = _c === void 0 ? 0 : _c,
      _d = _a.hover,
      enableHover = _d === void 0 ? true : _d,
      customHover = _a.customHover,
      _e = _a.maxWidth,
      maxWidth = _e === void 0 ? '300px' : _e,
      _f = _a.focusable,
      focusable = _f === void 0 ? false : _f,
      other = __rest(_a, ["placement", "children", "content", "delayIn", "delayOut", "hover", "customHover", "maxWidth", "focusable"]);

  var id = useUid();
  var theme = useTheme();

  var _g = React.useState(false),
      show = _g[0],
      setShow = _g[1];

  var tooltipColor = theme.colors.palette.blue.base;

  var _h = React.useState(false),
      hovering = _h[0],
      setHovering = _h[1];

  var _j = React.useState(false),
      delayShow = _j[0],
      setDelayShow = _j[1];

  function renderTrigger(_a) {
    var _b;

    var ref = _a.ref;
    var child = React.Children.only(children);
    return React.cloneElement(child, (_b = {
      ref: ref,
      'aria-describedby': id
    }, _b[!focusable ? 'onMouseEnter' : 'onFocus'] = function () {
      return setShow(true);
    }, _b[!focusable ? 'onMouseLeave' : 'onBlur'] = function () {
      return setShow(false);
    }, _b));
  }

  function onMouseLeave() {
    setHovering(false);
  }

  function onMouseEnter() {
    setHovering(true);
  }

  React.useEffect(function () {
    var timeoutIn;
    var timeoutOut;

    if (show) {
      timeoutIn = setTimeout(function () {
        setDelayShow(true);
      }, delayIn);
    } else {
      timeoutOut = setTimeout(function () {
        setDelayShow(false);
      }, delayOut);
    }

    return function () {
      if (timeoutIn) {
        clearInterval(timeoutIn);
      }

      if (timeoutOut) {
        clearInterval(timeoutOut);
      }
    };
  }, [delayIn, delayOut, show]);
  return jsx(Positioner, {
    placement: placement,
    isOpen: typeof customHover !== 'undefined' ? customHover : delayShow || hovering && enableHover,
    target: renderTrigger
  }, function (_a, state) {
    var placement = _a.placement,
        ref = _a.ref,
        style = _a.style,
        arrowProps = _a.arrowProps;
    return jsx(animated.div, __assign({
      id: id,
      "data-placement": placement,
      role: 'tooltip',
      className: 'Tooltip',
      ref: ref,
      style: __assign(__assign({
        margin: 0
      }, style), {
        opacity: state
      }),
      css: {
        zIndex: theme.zIndices.tooltip
      },
      onMouseEnter: onMouseEnter,
      onMouseLeave: onMouseLeave
    }, other), jsx("div", {
      className: 'Tooltip__content',
      "data-placement": placement,
      css: arrowStyles(tooltipColor),
      ref: arrowProps.ref,
      style: arrowProps.style
    }), jsx(Text, {
      className: 'Tooltip__text',
      variant: 'body',
      css: [{
        margin: 0,
        fontSize: theme.fontSizes[1],
        display: 'inline-block',
        textAlign: 'center',
        maxWidth: maxWidth,
        borderRadius: theme.radii.md,
        padding: "".concat(theme.spaces.md, " ").concat(theme.spaces.lg),
        color: 'white',
        background: tooltipColor
      }]
    }, content));
  });
};